import {
  ButtonType,
  ButtonWidget,
  CompactInlineAlertWidget,
  InlineAlertType,
  TextFieldWidget
} from '@tableau/widgets';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { openReactConfirmationDialog } from '~src/commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';

import { JwtPreviewModal } from './JwtPreviewModal';
import { AuthFederationService, IAuthFederationConnector } from './service/AuthFederationService';
import { connectorClassLabel, WIF_CONNECTOR_CLASSES } from './WifConnectorClasses';

export const WIF_CONNECTORS_SECTION_TEST_ID = 'wif-connectors-section';
export const WIF_CONNECTORS_TABLE_TEST_ID = 'wif-connectors-table';
export const WIF_CONNECTORS_LOADING_TEST_ID = 'wif-connectors-loading';
export const WIF_CONNECTORS_LOAD_ERROR_TEST_ID = 'wif-connectors-load-error';
export const WIF_CONNECTORS_ADD_BUTTON_TEST_ID = 'wif-connectors-add-button';
export const WIF_CONNECTORS_CREATE_ERROR_TEST_ID = 'wif-connectors-create-error';
export const WIF_CONNECTORS_DUPLICATE_ERROR_TEST_ID = 'wif-connectors-duplicate-error';
export const WIF_CONNECTORS_INVALID_AUDIENCE_ERROR_TEST_ID = 'wif-connectors-invalid-audience-error';
export const WIF_CONNECTORS_ADD_FORM_CLASS_SELECT_TEST_ID = 'wif-connectors-add-class-select';
export const WIF_CONNECTORS_ADD_FORM_AUDIENCE_INPUT_TEST_ID = 'wif-connectors-add-audience-input';
export const WIF_CONNECTORS_ADD_FORM_SUBMIT_TEST_ID = 'wif-connectors-add-submit';
export const WIF_CONNECTORS_ADD_FORM_CANCEL_TEST_ID = 'wif-connectors-add-cancel';
export const WIF_CONNECTORS_PREVIEW_JWT_BUTTON_TEST_ID = 'wif-connectors-preview-jwt-button';

export function wifConnectorRowTestId(luid: string): string {
  return `wif-connector-row-${luid}`;
}
export function wifConnectorDeleteTestId(luid: string): string {
  return `wif-connector-delete-${luid}`;
}
export function wifConnectorAudienceCellTestId(luid: string): string {
  return `wif-connector-audience-cell-${luid}`;
}


type CreateErrorKind = 'duplicate' | 'invalidAudience' | 'generic';
interface CreateError {
  kind: CreateErrorKind;
  message: string;
}

function createErrorTestId(kind: CreateErrorKind): string {
  switch (kind) {
    case 'duplicate':
      return WIF_CONNECTORS_DUPLICATE_ERROR_TEST_ID;
    case 'invalidAudience':
      return WIF_CONNECTORS_INVALID_AUDIENCE_ERROR_TEST_ID;
    default:
      return WIF_CONNECTORS_CREATE_ERROR_TEST_ID;
  }
}

export interface WifConnectorsSectionProps {
  issuerUrl: string;
  tokenLifetimeSeconds: number;
}

export function WifConnectorsSection(props: WifConnectorsSectionProps): JSX.Element {
  const [connectors, setConnectors] = React.useState<IAuthFederationConnector[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(false);

  const [showAddForm, setShowAddForm] = React.useState(false);
  const [addConnectorClass, setAddConnectorClass] = React.useState('');
  const [addAudience, setAddAudience] = React.useState('');
  const [creating, setCreating] = React.useState(false);
  const [createError, setCreateError] = React.useState<CreateError | null>(null);
  const [showJwtPreview, setShowJwtPreview] = React.useState(false);

  React.useEffect(() => {
    AuthFederationService.listConnectors()
      .then((c) => setConnectors(c))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  const handleAddClick = () => {
    setShowAddForm(true);
    setAddConnectorClass('');
    setAddAudience('');
    setCreateError(null);
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
    setAddConnectorClass('');
    setAddAudience('');
    setCreateError(null);
  };

  const handleCreate = () => {
    if (!addConnectorClass || !addAudience) {
      return;
    }
    setCreateError(null);
    setCreating(true);
    AuthFederationService.createConnector(addConnectorClass, addAudience)
      .then((connector) => {
        setConnectors((prev) => [...prev, connector]);
        setShowAddForm(false);
        setAddConnectorClass('');
        setAddAudience('');
      })
      .catch((error: any) => {
        const errorCode = error?.data?.error?.code;
        if (errorCode === '503927' || errorCode === 503927) {
          setCreateError({ kind: 'duplicate', message: Messages.authFederationConnectorDuplicate() });
        } else if (errorCode === '503925' || errorCode === 503925) {
          setCreateError({ kind: 'invalidAudience', message: Messages.authFederationConnectorInvalidAudience() });
        } else {
          setCreateError({ kind: 'generic', message: Messages.authFederationConnectorCreateError() });
        }
      })
      .finally(() => setCreating(false));
  };

  const handleDelete = (connector: IAuthFederationConnector) => {
    const className = connectorClassLabel(connector.connectorClass);
    openReactConfirmationDialog<string, void>({
      message: Messages.authFederationConnectorDeleteConfirm({ CLASS: className }),
      paramsForAction: connector.luid,
      actionName: Messages.authFederationConnectorDelete(),
      isActionDelete: true,
      dialogWidth: TabDialogWidth.width375px,
      action: (luidToDelete) =>
        AuthFederationService.deleteConnector(luidToDelete).then(() => {
          setConnectors((prev) => prev.filter((c) => c.luid !== luidToDelete));
        })
    });
  };

  if (loading) {
    return (
      <div style={{ marginTop: '16px' }} data-tb-test-id={WIF_CONNECTORS_SECTION_TEST_ID}>
        <div data-tb-test-id={WIF_CONNECTORS_LOADING_TEST_ID}>
          {Messages.authFederationConnectorsLoading()}
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={{ marginTop: '16px' }} data-tb-test-id={WIF_CONNECTORS_SECTION_TEST_ID}>
        <CompactInlineAlertWidget type={InlineAlertType.Error} testId={WIF_CONNECTORS_LOAD_ERROR_TEST_ID}>
          <div>{Messages.authFederationConnectorsLoadError()}</div>
        </CompactInlineAlertWidget>
      </div>
    );
  }

  return (
    <div style={{ marginTop: '16px' }} data-tb-test-id={WIF_CONNECTORS_SECTION_TEST_ID}>
      <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>{Messages.authFederationConnectorsTitle()}</div>

      {!showAddForm && (
        <div style={{ marginBottom: '8px' }}>
          <ButtonWidget
            buttonType={ButtonType.Outline}
            testId={WIF_CONNECTORS_ADD_BUTTON_TEST_ID}
            type='button'
            onClick={handleAddClick}
          >
            {Messages.authFederationConnectorAddButton()}
          </ButtonWidget>
          <ButtonWidget
            buttonType={ButtonType.Outline}
            testId={WIF_CONNECTORS_PREVIEW_JWT_BUTTON_TEST_ID}
            type='button'
            onClick={() => setShowJwtPreview(true)}
            style={{ marginLeft: '8px' }}
          >
            {Messages.authFederationJwtPreviewButton()}
          </ButtonWidget>
        </div>
      )}

      {showJwtPreview && (
        <JwtPreviewModal
          onClose={() => setShowJwtPreview(false)}
          issuerUrl={props.issuerUrl}
          tokenLifetimeSeconds={props.tokenLifetimeSeconds}
          connectors={connectors}
        />
      )}

      {showAddForm && (
        <div style={{ marginBottom: '16px', padding: '12px', border: '1px solid #ccc', borderRadius: '4px' }}>
          <div style={{ marginBottom: '8px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
              {Messages.authFederationConnectorClass()}
            </label>
            <select
              data-tb-test-id={WIF_CONNECTORS_ADD_FORM_CLASS_SELECT_TEST_ID}
              value={addConnectorClass}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setAddConnectorClass(e.target.value)}
              disabled={creating}
              style={{ width: '100%', padding: '6px', fontSize: '14px' }}
            >
              <option value=''>{Messages.authFederationConnectorClassPlaceholder()}</option>
              {WIF_CONNECTOR_CLASSES.map((connectorClass) => (
                <option key={connectorClass} value={connectorClass}>
                  {connectorClassLabel(connectorClass)}
                </option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: '8px' }}>
            <TextFieldWidget
              testId={WIF_CONNECTORS_ADD_FORM_AUDIENCE_INPUT_TEST_ID}
              label={Messages.authFederationConnectorAudience()}
              value={addAudience}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddAudience(e.target.value)}
              placeholder={Messages.authFederationConnectorAudiencePlaceholder()}
              disabled={creating}
            />
          </div>
          {createError && (
            <CompactInlineAlertWidget
              type={InlineAlertType.Error}
              testId={createErrorTestId(createError.kind)}
            >
              <div>{createError.message}</div>
            </CompactInlineAlertWidget>
          )}
          <div style={{ marginTop: '8px' }}>
            <ButtonWidget
              buttonType={ButtonType.Primary}
              testId={WIF_CONNECTORS_ADD_FORM_SUBMIT_TEST_ID}
              type='button'
              disabled={creating || !addConnectorClass || !addAudience}
              onClick={handleCreate}
              style={{ marginRight: '8px' }}
            >
              {Messages.authFederationConnectorAddButton()}
            </ButtonWidget>
            <ButtonWidget
              buttonType={ButtonType.Outline}
              testId={WIF_CONNECTORS_ADD_FORM_CANCEL_TEST_ID}
              type='button'
              disabled={creating}
              onClick={handleCancelAdd}
            >
              {Messages.authFederationConnectorCancel()}
            </ButtonWidget>
          </div>
        </div>
      )}

      {connectors.length === 0 && !showAddForm && (
        <div style={{ fontStyle: 'italic', color: '#666' }}>No connectors configured</div>
      )}

      {connectors.length > 0 && (
        <table data-tb-test-id={WIF_CONNECTORS_TABLE_TEST_ID} style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ccc', whiteSpace: 'nowrap', fontWeight: 'bold' }}>
                {Messages.authFederationConnectorClass()}
              </th>
              <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ccc', fontWeight: 'bold' }}>
                {Messages.authFederationConnectorAudience()}
              </th>
              <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ccc' }}></th>
            </tr>
          </thead>
          <tbody>
            {connectors.map((connector) => {
              const className = connectorClassLabel(connector.connectorClass);
              return (
                <tr key={connector.luid} data-tb-test-id={wifConnectorRowTestId(connector.luid)}>
                  <td style={{ padding: '8px', borderBottom: '1px solid #eee', whiteSpace: 'nowrap', fontWeight: 'bold' }}>{className}</td>
                  <td
                    data-tb-test-id={wifConnectorAudienceCellTestId(connector.luid)}
                    style={{ padding: '8px', borderBottom: '1px solid #eee', wordBreak: 'break-all' }}
                  >
                    {connector.audience}
                  </td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #eee', whiteSpace: 'nowrap' }}>
                    <ButtonWidget
                      buttonType={ButtonType.Destructive}
                      testId={wifConnectorDeleteTestId(connector.luid)}
                      type='button'
                      onClick={() => handleDelete(connector)}
                      style={{ fontSize: '12px', padding: '4px 8px' }}
                    >
                      {Messages.authFederationConnectorDelete()}
                    </ButtonWidget>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
